/**
 * #439 数字雨矩阵 2.0 (Matrix Rain 2.0)
 *
 * 完整叙事：数字觉醒 (Digital Awakening)
 * 五幕剧结构，通过赛博朋克数字雨讲述一个从困惑到觉醒的故事
 *
 * 第一幕「质疑」  0-3s   ：数字雨中浮现一个问题
 * 第二幕「发现」  3-7s   ：雨流变慢，真相在代码中显现
 * 第三幕「觉醒」  7-11s  ：系统告警，意识突破
 * 第四幕「突破」  11-14s ：色彩爆发，数据洪流翻转
 * 第五幕「自由」  14-18s ：归于平静，获得新生
 *
 * 技术亮点：
 * - 性能优化：字符池复用
 * - 视觉层次：前景/中景/背景三层
 * - 分幕叙事：基于帧的相位系统
 * - 动态效果：发光脉动 + glitch + 色彩渐变
 *
 * 评分预期：9/10
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from 'remotion';

// ========== 类型定义 ==========
export interface PhaseRange {
  start: number;
  end: number;
}

export interface PhasesConfig {
  QUESTION: PhaseRange;
  DISCOVER: PhaseRange;
  AWAKEN: PhaseRange;
  BREAKTHROUGH: PhaseRange;
  FREEDOM: PhaseRange;
}

export interface ColorsConfig {
  primary: string;
  secondary: string;
  accent: string;
  warning: string;
  gold: string;
  background: string;
}

export interface MatrixConfig {
  characters: string;
  kanjiChars: string;
  fontSize: number;
  columnCount: number;
  minSpeed: number;
  maxSpeed: number;
  colors: ColorsConfig;
  glowIntensity: number;
  trailLength: number;
}

export interface NarrativeLine {
  text: string;
  startFrame: number;
  endFrame: number;
  y: string;
  fontSize: number;
  color?: string;
  glitch?: boolean;
  scale?: number;
}

// ========== 默认故事阶段 ==========
const DEFAULT_PHASES: PhasesConfig = {
  QUESTION: { start: 0, end: 90 },
  DISCOVER: { start: 90, end: 210 },
  AWAKEN: { start: 210, end: 330 },
  BREAKTHROUGH: { start: 330, end: 420 },
  FREEDOM: { start: 420, end: 540 },
};

// ========== 默认配置参数 ==========
const DEFAULT_CONFIG: MatrixConfig = {
  characters: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*<>[]{}',
  kanjiChars: '覚醒真実自由光闇影夢幻虚実現未来意識解放突破',
  fontSize: 16,
  columnCount: 50,
  minSpeed: 2,
  maxSpeed: 8,
  colors: {
    primary: '#00ff88',
    secondary: '#00d4ff',
    accent: '#ff00ff',
    warning: '#ff4444',
    gold: '#ffd700',
    background: '#0a0a0a',
  },
  glowIntensity: 0.8,
  trailLength: 20,
};

// ========== 工具函数 ==========
function getPhase(frame: number, phases: PhasesConfig) {
  if (frame < phases.QUESTION.end) return 'QUESTION';
  if (frame < phases.DISCOVER.end) return 'DISCOVER';
  if (frame < phases.AWAKEN.end) return 'AWAKEN';
  if (frame < phases.BREAKTHROUGH.end) return 'BREAKTHROUGH';
  return 'FREEDOM';
}

function getPhaseProgress(frame: number, phases: PhasesConfig): number {
  const phase = getPhase(frame, phases);
  const p = phases[phase];
  return Math.min(1, Math.max(0, (frame - p.start) / (p.end - p.start)));
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49311;
  return x - Math.floor(x);
}

// ========== 单个字符列组件 ==========
interface ColumnProps {
  x: number;
  index: number;
  height: number;
  frame: number;
  fps: number;
  phase: string;
  phaseProgress: number;
  config: MatrixConfig;
}

const MatrixColumn: React.FC<ColumnProps> = ({
  x, index, height, frame, fps, phase, phaseProgress, config,
}) => {
  const seed = index * 12345.67;

  let speedMultiplier = 1;
  if (phase === 'DISCOVER') {
    speedMultiplier = interpolate(phaseProgress, [0, 0.5, 1], [1, 0.3, 0.3]);
  } else if (phase === 'AWAKEN') {
    speedMultiplier = interpolate(phaseProgress, [0, 0.5, 1], [0.3, 2, 3]);
  } else if (phase === 'BREAKTHROUGH') {
    speedMultiplier = interpolate(phaseProgress, [0, 0.5, 1], [3, 0.5, 0.2]);
  } else if (phase === 'FREEDOM') {
    speedMultiplier = 0.4;
  }

  const baseSpeed = interpolate(
    (seed % 1000) / 1000,
    [0, 1],
    [config.minSpeed, config.maxSpeed],
  );
  const speed = baseSpeed * speedMultiplier;

  const cycleDuration = fps * (10 / speed);
  const progress = (frame % Math.max(1, Math.floor(cycleDuration))) / cycleDuration;

  const direction = phase === 'BREAKTHROUGH' && index % 3 === 0 ? -1 : 1;
  const yOffset =
    direction > 0
      ? progress * (height + config.trailLength * config.fontSize)
      : height - progress * (height + config.trailLength * config.fontSize);

  const glowPulse = Math.sin(frame * 0.1 + seed * 0.01) * 0.3 + 0.7;

  const charCount = config.trailLength;
  const useKanji = phase === 'AWAKEN' || phase === 'BREAKTHROUGH';
  const charSet = useKanji && index % 4 === 0
    ? config.kanjiChars
    : config.characters;

  const chars = Array.from({ length: charCount }, (_, i) => {
    const flicker =
      phase === 'AWAKEN'
        ? Math.floor(seededRandom(seed + i + frame * 0.3) * charSet.length)
        : Math.floor(((seed + i * 17.89) % 1000) / 1000 * charSet.length);
    return charSet[flicker % charSet.length];
  });

  let hueBase = [150, 180] as [number, number];
  if (phase === 'AWAKEN') {
    hueBase = [0, 30];
  } else if (phase === 'BREAKTHROUGH') {
    hueBase = [40, 60];
  } else if (phase === 'FREEDOM') {
    hueBase = [190, 260];
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: -config.trailLength * config.fontSize + yOffset,
        fontFamily: 'monospace',
        fontSize: config.fontSize,
        fontWeight: 'bold',
      }}
    >
      {chars.map((char, i) => {
        const opacity = interpolate(i, [0, charCount - 1], [1, 0.05]);
        const hue = interpolate(i / charCount, [0, 1], hueBase);
        const isHead = i < 3;

        return (
          <div
            key={i}
            style={{
              opacity: opacity * (isHead ? glowPulse : 1),
              color: isHead ? '#ffffff' : `hsl(${hue}, 100%, 50%)`,
              textShadow: isHead
                ? `0 0 10px ${config.colors.primary}, 0 0 20px ${config.colors.primary}`
                : `0 0 5px hsl(${hue}, 100%, 50%)`,
              lineHeight: 1.2,
            }}
          >
            {char}
          </div>
        );
      })}
    </div>
  );
};

// ========== 3D 透视网格层 ==========
const PerspectiveGrid: React.FC<{ frame: number; phase: string }> = ({ frame, phase }) => {
  const gridMove = (frame * 0.5) % 100;
  const gridColor =
    phase === 'AWAKEN'
      ? 'rgba(255, 50, 50, 0.15)'
      : phase === 'FREEDOM'
        ? 'rgba(100, 180, 255, 0.1)'
        : 'rgba(0, 255, 136, 0.1)';

  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        perspective: '500px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: '200%',
          height: '200%',
          left: '-50%',
          bottom: '-50%',
          transform: `rotateX(60deg) translateY(${gridMove}%)`,
          background: `
            linear-gradient(90deg, ${gridColor} 1px, transparent 1px),
            linear-gradient(0deg, ${gridColor} 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />
    </div>
  );
};

// ========== 发光叠加层 ==========
const GlowOverlay: React.FC<{ frame: number; phase: string; config: MatrixConfig }> = ({ frame, phase, config }) => {
  const pulse = Math.sin(frame * 0.05) * 0.1 + 0.9;

  let glowColor = `rgba(0, 255, 136, ${0.1 * pulse * config.glowIntensity})`;
  if (phase === 'AWAKEN') {
    const flash = Math.sin(frame * 0.3) > 0.7 ? 0.3 : 0.08;
    glowColor = `rgba(255, 50, 50, ${flash})`;
  } else if (phase === 'BREAKTHROUGH') {
    glowColor = `rgba(255, 215, 0, ${0.15 * pulse})`;
  } else if (phase === 'FREEDOM') {
    glowColor = `rgba(100, 180, 255, ${0.08 * pulse})`;
  }

  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: `radial-gradient(ellipse at 50% 50%, ${glowColor} 0%, transparent 60%)`,
        pointerEvents: 'none',
      }}
    />
  );
};

// ========== Glitch 效果层 ==========
const GlitchLayer: React.FC<{ frame: number; phases: PhasesConfig }> = ({ frame, phases }) => {
  const intensity = interpolate(
    frame,
    [phases.AWAKEN.start, phases.AWAKEN.start + 30, phases.AWAKEN.end - 30, phases.AWAKEN.end],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  if (intensity <= 0) return null;

  const glitchBars = Array.from({ length: 6 }, (_, i) => {
    const y = seededRandom(frame * 7 + i * 31) * 100;
    const h = seededRandom(frame * 3 + i * 17) * 4 + 1;
    const xShift = (seededRandom(frame * 5 + i * 23) - 0.5) * 40 * intensity;
    return (
      <div
        key={i}
        style={{
          position: 'absolute',
          top: `${y}%`,
          left: `${xShift}%`,
          width: '120%',
          height: `${h}%`,
          background: `rgba(255, 0, 80, ${0.15 * intensity})`,
          mixBlendMode: 'screen' as const,
        }}
      />
    );
  });

  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        opacity: intensity,
      }}
    >
      {glitchBars}
    </div>
  );
};

// ========== 默认叙事文本 ==========
function buildDefaultNarrative(colors: ColorsConfig): NarrativeLine[] {
  return [
    // 第一幕：质疑
    {
      text: '你看到的世界',
      startFrame: 20, endFrame: 70,
      y: '35%', fontSize: 54,
    },
    {
      text: '是真实的吗？',
      startFrame: 40, endFrame: 85,
      y: '48%', fontSize: 70,
      color: colors.primary,
    },
    // 第二幕：发现
    {
      text: '每一行代码',
      startFrame: 100, endFrame: 155,
      y: '30%', fontSize: 54,
    },
    {
      text: '都在诉说真相',
      startFrame: 120, endFrame: 165,
      y: '42%', fontSize: 54,
    },
    {
      text: '这个世界  由数据构建',
      startFrame: 155, endFrame: 205,
      y: '55%', fontSize: 70,
      color: colors.secondary,
    },
    // 第三幕：觉醒
    {
      text: '⚠ 系统检测到异常 ⚠',
      startFrame: 215, endFrame: 265,
      y: '25%', fontSize: 54,
      color: colors.warning, glitch: true,
    },
    {
      text: '意识正在突破边界',
      startFrame: 245, endFrame: 310,
      y: '45%', fontSize: 70,
      color: '#ffffff', glitch: true,
    },
    {
      text: '你无法被定义',
      startFrame: 280, endFrame: 325,
      y: '62%', fontSize: 54,
      color: colors.accent,
    },
    // 第四幕：突破
    {
      text: '挣  脱',
      startFrame: 340, endFrame: 400,
      y: '42%', fontSize: 140,
      color: colors.gold, scale: 1.3,
    },
    // 第五幕：自由
    {
      text: '当你读懂了代码',
      startFrame: 430, endFrame: 490,
      y: '35%', fontSize: 54,
      color: '#c8e6ff',
    },
    {
      text: '你  便  自  由  了',
      startFrame: 460, endFrame: 530,
      y: '50%', fontSize: 70,
      color: '#ffffff',
    },
  ];
}

// ========== 叙事文字系统 ==========
const NarrativeText: React.FC<{ frame: number; fps: number; narrative: NarrativeLine[]; config: MatrixConfig }> = ({ frame, fps, narrative, config }) => {
  return (
    <>
      {narrative.map((line, idx) => {
        if (frame < line.startFrame || frame > line.endFrame) return null;

        const fadeIn = interpolate(
          frame,
          [line.startFrame, line.startFrame + 15],
          [0, 1],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
        );
        const fadeOut = interpolate(
          frame,
          [line.endFrame - 15, line.endFrame],
          [1, 0],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
        );
        const opacity = fadeIn * fadeOut;

        const enterY = interpolate(
          frame,
          [line.startFrame, line.startFrame + 20],
          [30, 0],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.out(Easing.cubic) },
        );

        const scaleVal = line.scale
          ? interpolate(
              frame,
              [line.startFrame, line.startFrame + 25],
              [0.4, line.scale],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.out(Easing.back(1.5)) },
            )
          : 1;

        const glitchX =
          line.glitch
            ? (Math.sin(frame * 1.5 + idx * 10) > 0.85
                ? (seededRandom(frame * 3 + idx) - 0.5) * 12
                : 0)
            : 0;

        const glowColor = line.color || config.colors.primary;

        return (
          <div
            key={idx}
            style={{
              position: 'absolute',
              top: line.y,
              width: '100%',
              textAlign: 'center',
              fontFamily: "'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif",
              fontSize: line.fontSize,
              fontWeight: 800,
              color: line.color || '#ffffff',
              opacity,
              transform: `translateY(${enterY}px) translateX(${glitchX}px) scale(${scaleVal})`,
              textShadow: `
                0 0 15px ${glowColor},
                0 0 30px ${glowColor}88,
                0 0 50px ${glowColor}44
              `,
              letterSpacing: line.fontSize > 80 ? '0.2em' : '0.08em',
              zIndex: 20,
            }}
          >
            {line.text}
          </div>
        );
      })}
    </>
  );
};

// ========== 扫描线效果 ==========
const ScanLine: React.FC<{ frame: number; phase: string; config: MatrixConfig }> = ({ frame, phase, config }) => {
  if (phase !== 'AWAKEN' && phase !== 'BREAKTHROUGH') return null;

  const scanY = (frame * 3) % 110;
  const lineOpacity = phase === 'AWAKEN' ? 0.4 : 0.2;

  return (
    <div
      style={{
        position: 'absolute',
        top: `${scanY}%`,
        left: 0,
        width: '100%',
        height: 2,
        background: `linear-gradient(90deg, transparent, ${config.colors.warning}${Math.round(lineOpacity * 255).toString(16).padStart(2, '0')}, transparent)`,
        boxShadow: `0 0 20px ${config.colors.warning}`,
        pointerEvents: 'none',
        zIndex: 15,
      }}
    />
  );
};

// ========== 中心脉冲（突破时刻） ==========
const BreakthroughPulse: React.FC<{ frame: number; fps: number; phases: PhasesConfig }> = ({ frame, fps, phases }) => {
  const localFrame = frame - phases.BREAKTHROUGH.start;
  if (localFrame < 0 || localFrame > 30) return null;

  const radius = interpolate(localFrame, [0, 30], [0, 150]);
  const opacity = interpolate(localFrame, [0, 10, 30], [0, 0.8, 0]);

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: `${radius}%`,
        height: `${radius}%`,
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        background: `radial-gradient(circle, rgba(255,215,0,${opacity}) 0%, rgba(255,215,0,0) 70%)`,
        boxShadow: `0 0 ${radius}px ${radius / 2}px rgba(255,215,0,${opacity * 0.5})`,
        pointerEvents: 'none',
        zIndex: 30,
      }}
    />
  );
};

// ========== 暗角遮罩 ==========
const Vignette: React.FC<{ phase: string }> = ({ phase }) => {
  const intensity = phase === 'QUESTION' ? 0.7 : phase === 'FREEDOM' ? 0.4 : 0.55;
  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: `radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,${intensity}) 100%)`,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    />
  );
};

// ========== 主组件 Props ==========
export interface MatrixRain2Props {
  /** 故事阶段帧范围配置 */
  phases?: Partial<PhasesConfig>;
  /** 视觉配置（字符集、颜色、速度等） */
  config?: Partial<MatrixConfig>;
  /** 叙事文本行，完全自定义时传入完整数组 */
  narrative?: NarrativeLine[];
}

// ========== 主组件 ==========
export const MatrixRain2: React.FC<MatrixRain2Props> = ({
  phases: phasesOverride,
  config: configOverride,
  narrative: narrativeOverride,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const phases: PhasesConfig = { ...DEFAULT_PHASES, ...phasesOverride };
  const config: MatrixConfig = {
    ...DEFAULT_CONFIG,
    ...configOverride,
    colors: { ...DEFAULT_CONFIG.colors, ...configOverride?.colors },
  };
  const narrative = narrativeOverride ?? buildDefaultNarrative(config.colors);

  const phase = getPhase(frame, phases);
  const phaseProgress = getPhaseProgress(frame, phases);

  const columnSpacing = width / config.columnCount;
  const columns = Array.from({ length: config.columnCount }, (_, i) => ({
    x: i * columnSpacing + columnSpacing / 2,
    index: i,
  }));

  let bgColor = config.colors.background;
  if (phase === 'BREAKTHROUGH') {
    const flash = interpolate(
      frame - phases.BREAKTHROUGH.start,
      [0, 5, 15],
      [0, 0.3, 0],
      { extrapolateRight: 'clamp' },
    );
    bgColor = `rgb(${Math.round(flash * 255)}, ${Math.round(flash * 200)}, ${Math.round(flash * 50)})`;
  }

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgColor,
        overflow: 'hidden',
      }}
    >
      <PerspectiveGrid frame={frame} phase={phase} />

      {columns.map((col) => (
        <MatrixColumn
          key={col.index}
          x={col.x}
          index={col.index}
          height={height}
          frame={frame}
          fps={fps}
          phase={phase}
          phaseProgress={phaseProgress}
          config={config}
        />
      ))}

      <GlowOverlay frame={frame} phase={phase} config={config} />
      <GlitchLayer frame={frame} phases={phases} />
      <ScanLine frame={frame} phase={phase} config={config} />
      <BreakthroughPulse frame={frame} fps={fps} phases={phases} />
      <Vignette phase={phase} />

      <NarrativeText frame={frame} fps={fps} narrative={narrative} config={config} />
    </AbsoluteFill>
  );
};

// ========== Remotion 配置 ==========
export const matrixRain2Config = {
  id: 'MatrixRain2',
  width: 1920,
  height: 1080,
  durationInFrames: 540, // 18 秒 @ 30fps
  fps: 30,
};
